import { getDateTimeFormatter } from '@fe/core';
import { GDFHotPollyTypes, GDFHotPolyHiRecordNormal } from '../../../gdf/type';
import HotPolyTitle from './common/HotPolyTitle';
import HotPolySuaTooltip from './HotPolySuaTooltip';
import HotPolyDescriptions from './common/HotPolyDescription';
// This is a mess...
const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW', 'N'];

export default function HotPolyHiTooltip({ record }: { record: GDFHotPolyHiRecordNormal }) {
    if (record.polyType === GDFHotPollyTypes.sua) {
        return <HotPolySuaTooltip record={record} />;
    }
    const formatter = getDateTimeFormatter();
    const altitude = (() => {
        let base = record.base;
        let tops = record.tops;

        if (record.polyType === GDFHotPollyTypes.sig_icing) {
            base = record.id;
            tops = record.dir;
            if (base) {
                base /= 10;
            }
            if (tops) {
                tops /= 10;
            }
        }

        if ((base === null || base === 0) && (tops === null || tops === 0)) {
            return '';
        }
        if (base !== 0 && base !== null && (tops === null || tops === 0)) {
            return `FL${(base * 10)?.toString().padStart(3, '0')} and above`;
        }
        if ((base === 0 || base === null) && tops !== null && tops !== 0) {
            return `At or below FL${(tops * 10).toString().padStart(3, '0')}`;
        }

        if (base !== 0 && base !== null && tops !== 0 && tops !== null) {
            return `FL${(base * 10)?.toString().padStart(3, '0')} to FL${(tops * 10).toString().padStart(3, '0')}`;
        }
        return '';
    })();

    const movement = (() => {
        // Because 0xff is not nullish enough
        if (record.dir === 0 && record.speed === 0) {
            return '';
        }

        if (record.dir !== null && record.speed !== null) {
            const dirString = dirs[Math.floor(((record.dir % 360) / 360) * (dirs.length - 1))];
            return `${record.dir}° (${dirString}) at ${record.speed} kts`;
        }
        if (record.dir !== null) {
            return `${record.dir}°`;
        }
        if (record.speed) {
            return `${record.speed} kts`;
        }
        return '';
    })();

    const severityLabel = getSeverityLabel(record.severity, record.polyType);

    return (
        <div>
            {import.meta.env.DEV && (
                <div className='font-mono'>
                    {GDFHotPollyTypes[record.polyType]} ({record.polyType})
                </div>
            )}

            <div>
                <HotPolyTitle type={record.polyType} id={record.id} direction={record.dir} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'max-content auto', columnGap: '1rem' }}>
                {record.label && (
                    <>
                        <div>ID:</div>
                        <div>{record.label}</div>
                    </>
                )}

                {shouldShowValidTime(record) && (
                    <>
                        <div>Valid:</div>
                        <div>
                            {formatter.formatTime(record.startTime, 'hm') || '-'} to {formatter.formatTime(record.endTime, 'hm') || '-'}
                        </div>
                    </>
                )}

                {!isPlaceholder(record.label) && (
                    <>
                        <div>Label:</div>
                        <div>{record.label}</div>
                    </>
                )}

                {record.issuedTime !== null && (
                    <>
                        <div>Issued:</div>
                        <div>{formatter.formatTime(record.issuedTime, 'hm')}</div>
                    </>
                )}

                {shouldShowType(record) && record.subType in subTypeLabels && (
                    <>
                        <div>Type:</div>
                        <div>{subTypeLabels[record.subType]}</div>
                    </>
                )}

                {shouldShowSeverity(record) && severityLabel && (
                    <>
                        <div>Severity:</div>
                        <div>{severityLabel}</div>
                    </>
                )}

                {record.polyType === GDFHotPollyTypes.tropical_cyclone && (
                    <>
                        <div>Winds:</div>
                        <div>{record.tops || '-'} kts</div>

                        <div>Speed:</div>
                        <div>{record.speed || '-'} kts</div>
                    </>
                )}

                {altitude && (
                    <>
                        <div>Altitude:</div>
                        <div>{altitude}</div>
                    </>
                )}

                {shouldShowMovement(record) && movement && (
                    <>
                        <div>Movement:</div>
                        <div>{movement}</div>
                    </>
                )}

                {record.coverage in coverageLabels && (
                    <>
                        <div>Coverage:</div>
                        <div>{coverageLabels[record.coverage]}</div>
                    </>
                )}

                {shouldShowOutlook(record) && record.outlook in outlookLabels && (
                    <>
                        <div>Outlook:</div>
                        <div>{outlookLabels[record.outlook]}</div>
                    </>
                )}
                <HotPolyDescriptions text={record.detail} polyType={record.polyType} />
            </div>
        </div>
    );
}

function shouldShowMovement(record: GDFHotPolyHiRecordNormal): boolean {
    return record.polyType !== GDFHotPollyTypes.sig_icing && record.polyType !== GDFHotPollyTypes.probsevere;
}

function shouldShowValidTime(record: GDFHotPolyHiRecordNormal): boolean {
    return (
        record.polyType !== GDFHotPollyTypes.airmet_freezing &&
        record.polyType !== GDFHotPollyTypes.airmet_icing &&
        record.polyType !== GDFHotPollyTypes.airmet_ifr &&
        record.polyType !== GDFHotPollyTypes.airmet_llws &&
        record.polyType !== GDFHotPollyTypes.airmet_mtnobsc &&
        record.polyType !== GDFHotPollyTypes.airmet_turb_aloft &&
        record.polyType !== GDFHotPollyTypes.airmet_turb_sfc &&
        record.polyType !== GDFHotPollyTypes.airmet_wind_sfc &&
        record.polyType !== GDFHotPollyTypes.sig_icing &&
        record.polyType !== GDFHotPollyTypes.probsevere
    );
}

function shouldShowSeverity(record: GDFHotPolyHiRecordNormal): boolean {
    return record.polyType !== GDFHotPollyTypes.hazard_turb_advisory;
}
function shouldShowType(record: GDFHotPolyHiRecordNormal): boolean {
    return record.polyType !== GDFHotPollyTypes.hazard_turb_advisory;
}

function shouldShowOutlook(record: GDFHotPolyHiRecordNormal): boolean {
    return record.polyType !== GDFHotPollyTypes.hazard_turb_advisory;
}

function isPlaceholder(input: string): boolean {
    return !input || input === '-';
}

const coverageLabels: Partial<Record<number, string>> = {
    [1]: 'SCT',
    [2]: 'SCT-BKN',
    [3]: 'BKN',
    [4]: 'SOLID'
};

const outlookLabels: Partial<Record<number, string>> = {
    [1]: 'No change',
    [2]: 'Decreasing',
    [3]: 'Increasing',
    [4]: 'Rapidly increasing'
};

// This depends on the type of the hot poly
const severityLabels: Partial<Record<number, string>> = {
    [1]: 'Trace',
    [2]: 'Trace, occasionally light',
    [3]: 'Light',
    [4]: 'Light, occasionally moderate',
    [5]: 'Moderate',
    [6]: 'Moderate, occasionally severe',
    [7]: 'Severe',
    [8]: 'Severe, occasionally extreme',
    [10]: 'Extreme'
};

const severityLabelsSigIcing: Partial<Record<number, string>> = {
    [4]: 'Light',
    [7]: 'Severe'
};

const ozoneSeverityLabels: Partial<Record<number, string>> = {
    [0]: 'less than 0.10',
    [1]: 'between 0.10 and 0.25ppm',
    [2]: 'greater than 0.25ppm'
};

const subTypeLabels: Partial<Record<number, string>> = {
    [1]: 'Normal',
    [2]: 'Mountain Wave',
    [3]: 'Low Level Wind Shear'
};

const tropicalStormSeverity: Record<number, string> = {
    [0]: '',
    [1]: '12 to 34 kts',
    [2]: '34 to 50 kts',
    [3]: '50 to < 64 kts',
    [4]: '>= 64kts'
};

function getSeverityLabel(severity: number, type: GDFHotPollyTypes): string | undefined {
    if (type === GDFHotPollyTypes.ozone) {
        return 'Concentration of Ozone above FL270: ' + ozoneSeverityLabels[severity];
    }

    if (type === GDFHotPollyTypes.wind_field) {
        return tropicalStormSeverity[severity] || '?';
    }

    if (type === GDFHotPollyTypes.sig_icing) {
        return severityLabelsSigIcing[severity] || '?';
    }

    return severityLabels[severity];
}
